import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRightIcon, SealCheckIcon } from "@phosphor-icons/react";
import { gbp, pct } from "@/lib/customerApi";

export function HoldoutBadge({ h }) {
  if (!h) return <span className="mono text-[9px] uppercase tracking-widest text-[#64748B]">2025 test pending</span>;
  return (
    <span className="inline-flex items-center gap-1 mono text-[9px] uppercase tracking-widest text-[#2ECA7F]">
      <SealCheckIcon size={12} weight="fill" /> 2025+ backtest: {gbp(h.net_profit)}
    </span>
  );
}

export function BotCard({ b, rank }) {
  return (
    <Link
      to={`/mt5/bot/${b.store_id}`}
      data-testid={`catalog-bot-${b.store_id}`}
      className="group relative border border-blue-900/40 bg-[#020617]/70 rounded-xl p-5 flex flex-col hover:border-[#38BDF8] hover:-translate-y-0.5 transition-[transform,border-color] duration-200"
    >
      <div className="flex items-center justify-between">
        <span className="mono text-[10px] uppercase tracking-widest text-[#FBBF24]">#{rank} on {b.symbol}</span>
        <span className="mono text-[10px] text-[#64748B]">{b.period} · <span className="text-[#FBBF24]">backtest</span></span>
      </div>
      <div className="mt-2 font-medium text-[#F1F5F9]">{b.name}</div>
      <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
        <div><div className="text-[#64748B] mono text-[9px] uppercase">Avg / month</div><div className="text-[#2ECA7F] text-base">{gbp(b.avg_monthly)}</div></div>
        <div><div className="text-[#64748B] mono text-[9px] uppercase">Max DD</div><div className="text-base">{pct(b.max_dd_pct)}</div></div>
        <div><div className="text-[#64748B] mono text-[9px] uppercase">Profit factor</div><div className="text-base">{b.profit_factor ?? "-"}</div></div>
        <div><div className="text-[#64748B] mono text-[9px] uppercase">Trades</div><div>{Number(b.total_trades || 0).toLocaleString()}</div></div>
        <div><div className="text-[#64748B] mono text-[9px] uppercase">Win rate</div><div>{pct(b.win_rate)}</div></div>
        <div><div className="text-[#64748B] mono text-[9px] uppercase">Months +/-</div><div>{b.profit_months ?? "-"}/{b.losing_months ?? "-"}</div></div>
      </div>
      <div className="mt-4 pt-3 border-t border-blue-900/30 flex items-center justify-between">
        <HoldoutBadge h={b.holdout} />
        <ArrowRightIcon size={14} className="text-[#38BDF8] group-hover:translate-x-1 transition-transform" />
      </div>
    </Link>
  );
}

export default function Catalog({ data }) {
  const syms = data?.symbols || [];
  const [sel, setSel] = useState(null);
  const active = syms.find((s) => s.symbol === sel) || syms[0];
  return (
    <section id="bots" data-testid="mt5-catalog" className="scroll-mt-24 relative z-10 max-w-[1400px] mx-auto px-6 pb-16">
      <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#FBBF24]">Software catalogue · top 5 per symbol</div>
      <h2 className="text-3xl font-medium tracking-tight mt-2">The MT5 bots available to license.</h2>
      <p className="text-sm text-[#94A3B8] mt-3 max-w-3xl">
        All figures on these cards are <b className="text-[#FBBF24]">backtests (simulated)</b> - not real or demo trading. Ranked by net profit on a{" "}
        {data?.start_balance ? gbp(data.start_balance) : "£2,000"} real-tick backtest (Jan 2022 to Jan 2025, profits compounding), so they show the best results
        from a much larger set of tested bots. Each bot is then re-tested on 2025+ data it wasn't built on - bots with a positive result there are also shown in the
        section below. Listing a bot is not a recommendation to trade it or the instrument it trades.
      </p>
      {!data ? (
        <div className="mt-8 mono text-xs text-[#64748B]" data-testid="catalog-loading">Loading bots…</div>
      ) : syms.length === 0 ? (
        <div className="mt-8 mono text-xs text-[#64748B]" data-testid="catalog-empty">No bots listed yet.</div>
      ) : (
        <>
          <div className="mt-8 flex flex-wrap gap-2">
            {syms.map((s) => (
              <button
                key={s.symbol}
                data-testid={`catalog-tab-${s.symbol}`}
                onClick={() => setSel(s.symbol)}
                className={`mono text-[11px] uppercase tracking-widest px-4 py-2 rounded-full border transition-colors ${
                  active?.symbol === s.symbol ? "border-[#38BDF8] bg-[#38BDF8]/15 text-[#38BDF8]" : "border-blue-900/50 text-[#94A3B8] hover:text-white"
                }`}
              >
                {s.symbol} <span className="text-[#64748B]">· {s.bots.length}</span>
              </button>
            ))}
          </div>
          <div className="mt-2 text-xs text-[#64748B]">{active?.symbol_name}</div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {active?.bots.map((b, i) => <BotCard key={b.store_id} b={b} rank={i + 1} />)}
          </div>
        </>
      )}
    </section>
  );
}
